import { cn } from "~/lib/cn";
import type { CSSProperties } from "react";
import { Award, ExternalLink, Lightbulb } from "lucide-react";
import { Feature, hasFeature } from "~/lib/has-feature";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { ButtonLink } from "./ui/button";
import { CDNImage } from "./ui/cdn-image";
import { CoachProfileImage } from "./ui/coach-profile-image";
import { CoachStatusBadge } from "./ui/coach-status-badge";
import { CoachCardLock } from "./coach-card-lock";

import { MembershipPlan } from "~/lib/schemas";
import { deriveCoachStatuses } from "~/lib/coach-status";

// Minimal props interface for CoachCard - only defines fields actually used
export interface CoachCardMinimalData {
  coach: {
    id: string;
    username: string;
    fullName: string;
    membershipPlan: MembershipPlan;
    profilePictureUrl?: string | null;
    isFeaturedCoach?: boolean | null;
    isFoundingCoach?: boolean | null;
  };
  profile: {
    coachingTitle?: string | null;
    brandLogoUrl?: string | null;
    brandName?: string | null;
    title?: string | null;
  } | null;
  similarity?: number;
  expertise?: string;
}

export interface CoachCardProps {
  coachData: CoachCardMinimalData;
  isActive?: boolean;
  isEnabled?: boolean;
  style?: CSSProperties;
  className?: string;
  href?: string;
  onLockClick?: () => void;
}

export const CoachCard = ({
  coachData,
  isActive = false,
  isEnabled = true,
  style,
  className,
  ...props
}: CoachCardProps) => {
  const href = props.href ?? `/${coachData.coach.username}`;
  const statusBadges = deriveCoachStatuses({
    membershipPlan: coachData.coach.membershipPlan,
    isFeaturedCoach: coachData.coach.isFeaturedCoach,
    isFoundingCoach: coachData.coach.isFoundingCoach,
  });

  return (
    <Card
      key={coachData.coach.id}
      className={cn(
        "relative flex cursor-pointer flex-col justify-between",
        className,
        {
          "border-primary": isActive,
        },
      )}
      style={style}
    >
      <a
        className={cn("flex flex-col flex-1 min-h-0", {
          "blur-sm": !isEnabled,
        })}
        href={isEnabled ? href : ""}
        aria-disabled={!isEnabled}
      >
        <CardHeader>
          <div className="flex gap-4">
            <div className="relative h-20 w-20 flex-shrink-0">
              <CoachProfileImage
                coach={coachData.coach}
                profilePictureUrl={coachData.coach.profilePictureUrl}
                fullName={coachData.coach.fullName}
                score={
                  coachData.similarity
                    ? Math.round(coachData.similarity * 100)
                    : undefined
                }
              />
              {statusBadges.length > 0 && (
                <div className="absolute -right-2 top-0 flex flex-col items-end gap-1">
                  {statusBadges.map((status, index) => (
                    <CoachStatusBadge
                      key={status}
                      status={status}
                      size="sm"
                      className={cn(
                        "shadow-sm",
                        1 === index && "translate-x-1",
                        2 === index && "translate-x-2",
                      )}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-center">
              <CardTitle className="text-xl leading-none">
                {coachData.coach.fullName}
              </CardTitle>
              <p className="mt-1 text-sm text-gray-500 leading-none">
                {coachData.profile?.coachingTitle}
              </p>
              {coachData.profile &&
                hasFeature(coachData.coach, Feature.FancyProfile) &&
                coachData.profile.brandLogoUrl && (
                  <div className="flex w-full items-center justify-start gap-x-2 mt-2">
                    <CDNImage
                      src={coachData.profile.brandLogoUrl}
                      width={32}
                      height={32}
                      alt={`${coachData.profile.brandName || coachData.coach.fullName}'s brand logo`}
                      className="aspect-square size-8 shrink-0 grow-0 rounded-full border border-gray-200 object-cover"
                    />
                    {coachData.profile.brandName && (
                      <div className="text-text-secondary text-sm font-medium truncate">
                        {coachData.profile.brandName}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 gap-1.5 h-full py-2">
          {coachData.expertise && (
            <div className="flex gap-1 min-h-0">
              <Award className="text-brand-700 inline size-[1.25rem] flex-shrink-0" />
              <div className="text-text-secondary flex-1 text-sm truncate">
                {coachData.expertise}
              </div>
            </div>
          )}
          {coachData.profile?.title && (
            <div className="flex gap-1 text-sm min-h-0 flex-1">
              <Lightbulb className="text-brand-700 inline size-[1.25rem] flex-shrink-0" />
              <p className="text-text-secondary line-clamp-2 flex-1 overflow-hidden">
                {coachData.profile.title}
              </p>
            </div>
          )}
        </CardContent>
      </a>
      <CardFooter className="flex-shrink-0">
        <ButtonLink
          href={isEnabled ? `/${coachData.coach.username}` : ""}
          key={coachData.coach.id}
          target="_blank"
          className="border-secondary flex w-full shrink-0 items-center gap-2 rounded-md"
          variant="outline"
          aria-disabled={!isEnabled}
        >
          View Profile <ExternalLink />
        </ButtonLink>
      </CardFooter>
      {!isEnabled && <CoachCardLock onClick={props.onLockClick} />}
    </Card>
  );
};
